<template>
  <div class="card border-0 shadow-none rounded-1 mb-25">
    <div class="card-body p-xl-40">
      <a-table
        :columns="columns"
        :data-source="items"
        :row-key="'purchase_id'"
        :loading="loading"
        :pagination="pagination"
        :scroll="{ x: 1360 }"
        class="text-nowrap align-middle supplier-report-table"
        @change="handleChange"
      >
        <template #emptyText>
          <div class="py-4 text-center text-muted">{{ $t(`${i18nScope}.no_supplier_reports_found`) }}</div>
        </template>

        <template #bodyCell="{ column, record }">
          <template v-if="column && column.key === 'date'">
            {{ formatDate(record.created_at) }}
          </template>

          <template v-else-if="column && column.key === 'invoice_total'">
            ${{ formatAmount(record.invoice_total) }}
          </template>

          <template v-else-if="column && column.key === 'paid'">
            ${{ formatAmount(record.paid) }}
          </template>

          <template v-else-if="column && column.key === 'due'">
            ${{ formatAmount(record.due) }}
          </template>

          <template v-else-if="column && column.key === 'status'">
            <span class="status-badge" :class="getStatusClass(record.status)">
              {{ formatLabel(record.status) }}
            </span>
          </template>

          <template v-else-if="column && column.key === 'payment_status'">
            <span class="status-badge" :class="getPaymentStatusClass(record.payment_status)">
              {{ formatLabel(record.payment_status) }}
            </span>
          </template>
        </template>
      </a-table>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import type { TablePaginationConfig } from "ant-design-vue";
import type { SupplierReportItem } from "@/services/report";

export default defineComponent({
  name: "SupplierReport",
  props: {
    items: {
      type: Array as () => SupplierReportItem[],
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Object as () => TablePaginationConfig,
      required: true,
    },
    i18nScope: {
      type: String,
      default: "supplier_report_page",
    },
  },
  emits: ["table-change"],
  setup(props, { emit }) {
    const { t, locale } = useI18n();

    const columns = computed(() => [
      { title: t(`${props.i18nScope}.table_date`), key: "date", dataIndex: "created_at", width: 210, align: "center" as const },
      { title: t(`${props.i18nScope}.table_reference`), key: "reference_no", dataIndex: "reference_no", width: 150, align: "center" as const },
      { title: t(`${props.i18nScope}.table_supplier_code`), key: "supplier_code", dataIndex: "supplier_code", width: 160, align: "center" as const },
      { title: t(`${props.i18nScope}.table_supplier`), key: "supplier_name", dataIndex: "supplier_name", width: 220, align: "center" as const },
      { title: t(`${props.i18nScope}.table_qty`), key: "quantity", dataIndex: "quantity", width: 100, align: "center" as const },
      { title: t(`${props.i18nScope}.table_grand_total`), key: "invoice_total", dataIndex: "invoice_total", width: 150, align: "center" as const },
      { title: t(`${props.i18nScope}.table_paid`), key: "paid", dataIndex: "paid", width: 130, align: "center" as const },
      { title: t(`${props.i18nScope}.table_due`), key: "due", dataIndex: "due", width: 130, align: "center" as const },
      { title: t(`${props.i18nScope}.table_payment_method`), key: "payment_method", dataIndex: "payment_method", width: 170, align: "center" as const },
      { title: t(`${props.i18nScope}.table_status`), key: "status", dataIndex: "status", width: 130, align: "center" as const },
      { title: t(`${props.i18nScope}.table_payment_status`), key: "payment_status", dataIndex: "payment_status", width: 170, align: "center" as const },
    ]);

    const formatDate = (dateString?: string) => {
      if (!dateString) return t(`${props.i18nScope}.not_available`);
      return new Date(dateString).toLocaleString(locale.value === "km" ? "km-KH" : "en-GB", {
        timeZone: "Asia/Phnom_Penh",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    };

    const formatAmount = (value?: string | number) =>
      Number(value ?? 0).toLocaleString(locale.value === "km" ? "km-KH" : "en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

    const formatLabel = (value?: string) => {
      const text = String(value || "pending").toLowerCase().replace(/_/g, " ");
      const normalizedKey = text.replace(/\s+/g, "_");
      const translationKey = `${props.i18nScope}.status_${normalizedKey}`;
      const translated = t(translationKey);
      if (translated !== translationKey) return translated;

      return text.charAt(0).toUpperCase() + text.slice(1);
    };

    const normalizeStatus = (value?: string) => String(value || "pending").toLowerCase().replace(/\s+/g, "_");

    const getStatusClass = (value?: string) => {
      const normalized = normalizeStatus(value);
      if (["received", "completed", "approved"].includes(normalized)) return "status-received";
      if (["ordered", "pending", "draft"].includes(normalized)) return "status-pending";
      if (["cancelled", "rejected"].includes(normalized)) return "status-cancelled";
      return "status-neutral";
    };

    const getPaymentStatusClass = (value?: string) => {
      const normalized = normalizeStatus(value);
      if (normalized === "paid") return "payment-paid";
      if (["partial", "partially_paid"].includes(normalized)) return "payment-partial";
      return "payment-unpaid";
    };

    const handleChange = (pagination: TablePaginationConfig) => {
      emit("table-change", pagination.current || 1, pagination.pageSize || 10);
    };

    return {
      columns,
      formatDate,
      formatAmount,
      formatLabel,
      getStatusClass,
      getPaymentStatusClass,
      handleChange,
    };
  },
});
</script>

<style scoped>
.supplier-report-table :deep(.ant-table-cell) {
  vertical-align: middle;
}

.supplier-report-table :deep(.ant-table-thead > tr > th) {
  white-space: nowrap;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 96px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.status-received,
.status-completed {
  color: #1f9254;
  background: #e8f7ee;
}

.status-ordered,
.status-pending {
  color: #d48a00;
  background: #fff4db;
}

.status-cancelled {
  color: #d33c43;
  background: #fdecec;
}

.payment-paid {
  color: #1f9254;
  background: #e8f7ee;
}

.payment-partial {
  color: #d48a00;
  background: #fff4db;
}

.payment-unpaid,
.payment-due,
.payment-pending {
  color: #d33c43;
  background: #fdecec;
}
</style>
